import { AuthorizeResponse } from '@criipto/auth-js';
import { css } from '@emotion/css';
import { ThemeProvider } from '@emotion/react';
import { CircularProgress, createTheme } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Background from 'components/Background';
import PageHeader from 'components/PageHeader';
import { AUTH_FREE_ROUTES } from 'config/routes';
import en from 'date-fns/locale/en-GB';
import { AuthContext } from 'index';
import jwtDecode from 'jwt-decode';
import { AccessControl } from 'logicComponents/AccessControl';
import DenyAccessPage from 'pages/DenyAccessPage';
import EligibilityQuestionnairePage from 'pages/EligibilityQuestionnairePage';
import FoodDiaryPage from 'pages/FoodDiaryPage';
import MitIDLoginPage from 'pages/MitIDLoginPage';
import NewBookingPage from 'pages/NewBookingPage/NewBookingPage';
import QuestionnairePage from 'pages/QuestionnairePage';
import QuestionnariesList from 'pages/QuestionnariesListPage/QuestionnariesList';
import ServerErrorPage from 'pages/ServerErrorPage';
import TimeLinePage from 'pages/TimeLinePage';
import { useCallback, useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import {
    loadingEnd,
    loadingStart,
    setAuthToken,
    setViewStateErrorCode,
    setViewStateErrorText
} from 'store/globalSlice';
import { RootState } from 'store/store';
import { User, setIsAuthChecked, setParticipantViewState, setUser } from 'store/userSlice';
import { colors } from 'styles/colors';
import { ParticipantAPI } from 'utils/ParticipantAPI';
import { ViewState } from 'utils/viewstate';

const theme = createTheme({
    palette: {
        primary: {
            main: colors.primary,
        },
        text: {
            primary: colors.primaryText,
        },
    },
    typography: {
        fontFamily: ['Figtree', 'regular'].join(','),
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    padding: '10px',
                    textTransform: 'none',
                    fontSize: '18px',
                    color: 'white',
                },
                sizeSmall: {
                    height: '2rem',
                    fontSize: '16px',
                },
                sizeMedium: {
                    width: '15rem',
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: colors.primary,
                    lineHeight: '20px',
                },
            },
        },
        MuiBackdrop: {
            styleOverrides: {
                root: {
                    marginTop: '4.5rem',
                },
            },
        },
    },
});

const Loader = () => {
    const isLoading = useSelector((state: RootState) => state.global.isLoading);

    if (isLoading) {
        return (
            <div className={styles.globalDimmer}>
                <CircularProgress />
            </div>
        );
    }
    return null;
};

const App = () => {
    const auth = useContext(AuthContext);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const isAuthChecked = useSelector((state: RootState) => state.user.isAuthChecked);
    const tokenReducer = useSelector((state: RootState) => state.global.authToken);
    const user = useSelector((state: RootState) => state.user.user);
    const participantViewState = useSelector((state: RootState) => state.user.participantViewState);
    const openedQuestionnaire = useSelector((state: RootState) => state.questionaire.openedQuestionnarie);

    const viewStateErrorText = useSelector((state: RootState) => state.global.viewStateErrorText);
    const viewStateErrorCode = useSelector((state: RootState) => state.global.viewStateErrorCode);

    const getParticipantViewStatus = useCallback(async () => {
        try {
            if (tokenReducer) {
                dispatch(loadingStart());
                const participantViewStatus: any = await ParticipantAPI.getParticipantViewStatus(tokenReducer);
                dispatch(setParticipantViewState(participantViewStatus.data.viewState));
                // dispatch(setParticipantViewState(ViewState.SCREENING_PAGE));
            }
        } catch (error: any) {
            dispatch(setViewStateErrorText(error.response.data.message));
            dispatch(setViewStateErrorCode(error.response.data.statusCode));
            dispatch(setParticipantViewState(ViewState.SERVER_ERROR));
            dispatch(loadingEnd());
        } finally {
            dispatch(loadingEnd());
        }
    }, [dispatch, tokenReducer]);

    useEffect(() => {
        getParticipantViewStatus();
    }, [getParticipantViewStatus]);

    // Redirect user to the pages he must be on according to the participant view state
    useEffect(() => {
        if (participantViewState !== null) {
            const currentPath = window.location.pathname;

            // Check if we need to restore the intended path after login
            const intendedPath = sessionStorage.getItem('intendedPath');
            if (intendedPath) {
                sessionStorage.removeItem('intendedPath');
                navigate(intendedPath);
            }

            if (participantViewState === ViewState.SCREENING_PAGE) {
                navigate('/questionnaire');
            }
            else if (
                participantViewState === ViewState.INFO_MEETING_BOOK_PAGE ||
                participantViewState === ViewState.V0_MEETING_BOOK_PAGE ||
                participantViewState === ViewState.V1_MEETING_BOOK_PAGE) {
                navigate('/new-booking');
            } else if (
                participantViewState === ViewState.DENY_ACCESS_1 ||
                participantViewState === ViewState.DENY_ACCESS_2 ||
                participantViewState === ViewState.DENY_ACCESS_3 ||
                participantViewState === ViewState.DENY_ACCESS_4 ||
                participantViewState === ViewState.DENY_ACCESS_5
            ) {
                navigate('/deny-access');
            } else if (
                participantViewState === ViewState.ACCESS_TO_DASHBOARD &&
                currentPath !== '/time-line' &&
                currentPath !== '/questionnaries-list' &&
                currentPath !== '/questionnaire-json' &&
                currentPath !== '/new-booking' &&
                currentPath !== '/fooddiary'
            ) {
                navigate('/time-line');
            } else if (participantViewState === ViewState.SERVER_ERROR) {
                navigate('/server-error');
            }
        }
    }, [navigate, participantViewState]);

    // Automatically obtain user data and redirect to /login if necessary
    const checkAuthStatus = useCallback(() => {
        // Skip routes that are listend in AUTH_FREE_ROUTES
        if (!AUTH_FREE_ROUTES.map((route) => window.location.pathname.includes(route)).includes(true)) {
            dispatch(loadingStart());
            localStorage.setItem('lastLoginCheck', Date.now().toString());
            // dispatch(setIsAuthChecked(false));

            // Use match instead of checkSession on Safari and Firefox since least does not work
            let authVerifier: () => Promise<AuthorizeResponse | null> = () =>
                auth.checkSession({ nonce: Math.random().toString(36).substring(7), timeout: 1000 });
            if (navigator.userAgent.includes('Safari') || navigator.userAgent.includes('Firefox')) {
                authVerifier = () => auth.redirect.match();
            }

            authVerifier()
                .then((res) => {
                    if (res?.error === 'WWW-Authenticate') {
                        return;
                    }

                    if (!res) {
                        dispatch(setAuthToken(null));
                        dispatch(setUser(null));
                        // Store the intended destination before redirecting to login
                        const currentPath = window.location.pathname;
                        if (currentPath !== '/login' && currentPath !== '/') {
                            sessionStorage.setItem('intendedPath', currentPath);
                        }
                        navigate('/login');
                    } else if (res.id_token && res.claims && !user) {
                        dispatch(setUser(res.claims as any));
                        dispatch(setAuthToken(res.id_token));
                    }
                })
                .catch((err) => {
                    console.log(Object.keys(err).map((key) => key + ': ' + err[key]));

                    if (err.error === 'login_required') {
                        dispatch(setAuthToken(null));
                        dispatch(setUser(null));
                        // Store the intended destination before redirecting to login
                        const currentPath = window.location.pathname;
                        if (currentPath !== '/login' && currentPath !== '/') {
                            sessionStorage.setItem('intendedPath', currentPath);
                        }
                        navigate('/login');
                    }
                })
                .finally(() => dispatch(loadingEnd()));
        }
    }, [auth, dispatch, navigate, user]);

    useEffect(() => {
        // If user is not logged in and auth status is not checked yet, check it
        if (!isAuthChecked && !user) {
            //dispatch(loadingStart());
            // Check if id_token is present in localStorage and it is valid, save user data to redux store
            // Otherwise, check the SSO session status and redirect to /login if necessary
            const token = localStorage.getItem('id_token');
            if (token) {
                const claims = jwtDecode(token) as AuthorizeResponse;

                if (claims.exp * 1000 > Date.now()) {
                    dispatch(setUser(claims as User));
                    dispatch(setAuthToken(token));
                    dispatch(setIsAuthChecked(true));
                    dispatch(loadingEnd());
                    return;
                }
            }

            // If token is not valid, check the SSO session status
            // Only allow to check it once every 5 seconds to avoid rate limiting
            const lastLoginCheck = localStorage.getItem('lastLoginCheck');
            const logoutCheck = localStorage.getItem('logoutCheck');
            if (logoutCheck === null || Date.now() - parseInt(logoutCheck) > 1000 * 3) {
                if (lastLoginCheck && Date.now() - parseInt(lastLoginCheck) > 1000 * 5) {
                    checkAuthStatus();
                } else if (lastLoginCheck) {
                    setTimeout(checkAuthStatus, 1000 * 5 - (Date.now() - parseInt(lastLoginCheck)));
                } else {
                    checkAuthStatus();
                }
            }
            dispatch(setIsAuthChecked(true));
        }
    }, [checkAuthStatus, dispatch, isAuthChecked, user]);

    // const isEligible = useSelector((state: RootState) => state.global.isEligible);

    return (
        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={en}>
                <div className={styles.content}>
                    <Background />
                    <PageHeader />
                    <Routes>
                        <Route element={<AccessControl />}>
                            {/* {!isEligible && <Route path="*" element={<Navigate to="/questionnaire" replace />}/>} */}
                            {participantViewState === ViewState.SCREENING_PAGE &&
                                <Route path="/questionnaire" element={<EligibilityQuestionnairePage/>}/>
                            }
                            {(participantViewState === ViewState.DENY_ACCESS_1 ||
                              participantViewState === ViewState.DENY_ACCESS_2 ||
                              participantViewState === ViewState.DENY_ACCESS_3 ||
                              participantViewState === ViewState.DENY_ACCESS_4 ||
                              participantViewState === ViewState.DENY_ACCESS_5
                            ) &&
                                <Route path="/deny-access" element={<DenyAccessPage/>}/>
                            }
                            {participantViewState === ViewState.ACCESS_TO_DASHBOARD &&
                                <>
                                    <Route path="/time-line" element={<TimeLinePage />} />
                                    <Route path="/questionnaries-list" element={<QuestionnariesList />} />
                                    <Route path="/fooddiary" element={<FoodDiaryPage />} />
                                </>
                            }
                            {participantViewState === ViewState.SERVER_ERROR && (
                                <Route
                                    path="/server-error"
                                    element={<ServerErrorPage text={viewStateErrorText} status={viewStateErrorCode} />}
                                />
                            )}
                        </Route>
                        <Route path="/login" element={<MitIDLoginPage />} />
                        <Route
                            path="/questionnaire-json"
                            element={
                                <QuestionnairePage
                                    json={
                                        (openedQuestionnaire &&
                                            JSON.parse(openedQuestionnaire.questionnaireJson).Json) ??
                                        undefined
                                    }
                                    questionnaireId={(openedQuestionnaire && openedQuestionnaire.id) ?? undefined}
                                    version={(openedQuestionnaire && openedQuestionnaire.version) ?? undefined}
                                    questionnaireName={(openedQuestionnaire && openedQuestionnaire.name) ?? undefined}
                                    dataMentorQuestionnaireId={(openedQuestionnaire && openedQuestionnaire.dataMentorQuestionnaireId) ?? null}
                                />
                            }
                        />
                        <Route path="/new-booking" element={<NewBookingPage />} />
                        <Route
                            path="/"
                            element={participantViewState === null ? <Loader /> : <Navigate to="/time-line" replace />}
                        />
                        <Route
                            path="*"
                            element={participantViewState === null ? <Loader /> : <Navigate to="/" replace />}
                        />
                    </Routes>
                </div>
            </LocalizationProvider>
        </ThemeProvider>
    );
};

const styles = {
    content: css({
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    }),
    globalDimmer: css({
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'rgba(0, 0, 0, 0.1)',
        zIndex: '9999',
    }),
};

export default App;
